export type DesignTheme = "green" | "amber" | "white" | "blue";

export interface ThemeConfig {
  name: string;
  description: string;
  cssVars: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
  gradient?: {
    light: string;
    dark: string;
  };
  fonts?: {
    sans?: string;
    mono?: string;
  };
  borderRadius?: string;
  animatedBackground?: {
    light?: {
      type: "terrain" | "none";
      primaryColor: string;
    };
    dark?: {
      type: "crt" | "none";
      primaryColor: string;
    };
  };
}

/**
 * Pre-calculated CSS text for each theme and mode combination.
 * This eliminates the need to iterate through individual CSS variables
 * and allows us to apply all theme styles in a single DOM operation.
 */
export interface ThemeStyleCache {
  light: string;
  dark: string;
}

/**
 * Generate pre-calculated CSS text for a theme configuration.
 * This runs once at module initialization to optimize runtime performance.
 */
function generateThemeStyles(theme: ThemeConfig): ThemeStyleCache {
  const lightVars = Object.entries(theme.cssVars.light)
    .map(([key, value]) => `${key}:${value}`)
    .join(";");

  const darkVars = Object.entries(theme.cssVars.dark)
    .map(([key, value]) => `${key}:${value}`)
    .join(";");

  return {
    light: theme.gradient
      ? `${lightVars};--background-gradient:${theme.gradient.light}`
      : lightVars,
    dark: theme.gradient
      ? `${darkVars};--background-gradient:${theme.gradient.dark}`
      : darkVars,
  };
}

export const designThemes: Record<DesignTheme, ThemeConfig> = {
  green: {
    name: "Green (P1)",
    description: "Classic P1 green terminal aesthetic",
    gradient: {
      light:
        "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.85 0.15 145 / 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.85 0.12 145 / 0.2), transparent)",
      dark: "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.3 0.15 145 / 0.4), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.25 0.12 145 / 0.3), transparent)",
    },
    animatedBackground: {
      light: {
        type: "terrain",
        primaryColor: "oklch(0.45 0.18 145)",
      },
      dark: {
        type: "crt",
        primaryColor: "oklch(0.75 0.15 145)",
      },
    },
    cssVars: {
      light: {
        "--background": "oklch(0.98 0.01 145)",
        "--foreground": "oklch(0.15 0.02 145)",
        "--card": "oklch(1 0 0)",
        "--card-foreground": "oklch(0.15 0.02 145)",
        "--popover": "oklch(1 0 0)",
        "--popover-foreground": "oklch(0.15 0.02 145)",
        "--primary": "oklch(0.45 0.18 145)", // P1 Green
        "--primary-foreground": "oklch(0.99 0 0)",
        "--secondary": "oklch(0.53 0.15 145)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.99 0 0)",
        "--muted": "oklch(0.94 0.02 145)",
        "--muted-foreground": "oklch(0.5 0.05 145)",
        "--accent": "oklch(0.65 0.16 145)",
        "--accent-foreground": "oklch(0.15 0.02 145)",
        "--destructive": "oklch(0.6 0.28 25)",
        "--destructive-foreground": "oklch(0.99 0 0)",
        "--border": "oklch(0.85 0.05 145)",
        "--input": "oklch(0.85 0.05 145)",
        "--ring": "oklch(0.45 0.18 145)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.45 0.18 145)",
      },
      dark: {
        "--background": "oklch(0.08 0.01 145)", // Dark CRT background
        "--foreground": "oklch(0.95 0 0)", // White text for readability
        "--card": "oklch(0.12 0.02 145)",
        "--card-foreground": "oklch(0.95 0 0)", // White text
        "--popover": "oklch(0.12 0.02 145)",
        "--popover-foreground": "oklch(0.95 0 0)", // White text
        "--primary": "oklch(0.75 0.15 145)", // P1 Green
        "--primary-foreground": "oklch(0.15 0.02 145)", // Darkened for WCAG AA contrast on bright primary
        "--secondary": "oklch(0.52 0.12 145)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.95 0 0)", // White text
        "--muted": "oklch(0.2 0.03 145)",
        "--muted-foreground": "oklch(0.7 0 0)", // Light gray text
        "--accent": "oklch(0.65 0.14 145)",
        "--accent-foreground": "oklch(0.95 0 0)", // White text
        "--destructive": "oklch(0.55 0.28 25)",
        "--destructive-foreground": "oklch(0.95 0 0)",
        "--border": "oklch(0.25 0.05 145)",
        "--input": "oklch(0.25 0.05 145)",
        "--ring": "oklch(0.75 0.15 145)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.75 0.15 145)",
      },
    },
  },
  amber: {
    name: "Amber (P3)",
    description: "Warm P3 amber terminal glow",
    gradient: {
      light:
        "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.85 0.15 65 / 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.85 0.12 50 / 0.2), transparent)",
      dark: "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.3 0.15 65 / 0.4), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.25 0.12 50 / 0.3), transparent)",
    },
    animatedBackground: {
      light: {
        type: "terrain",
        primaryColor: "oklch(0.55 0.18 65)",
      },
      dark: {
        type: "crt",
        primaryColor: "oklch(0.75 0.15 65)",
      },
    },
    cssVars: {
      light: {
        "--background": "oklch(0.98 0.01 65)",
        "--foreground": "oklch(0.15 0.02 65)",
        "--card": "oklch(1 0 0)",
        "--card-foreground": "oklch(0.15 0.02 65)",
        "--popover": "oklch(1 0 0)",
        "--popover-foreground": "oklch(0.15 0.02 65)",
        "--primary": "oklch(0.55 0.18 65)", // P3 Amber
        "--primary-foreground": "oklch(0.99 0 0)",
        "--secondary": "oklch(0.56 0.15 50)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.99 0 0)",
        "--muted": "oklch(0.94 0.02 65)",
        "--muted-foreground": "oklch(0.5 0.05 65)",
        "--accent": "oklch(0.7 0.16 55)",
        "--accent-foreground": "oklch(0.15 0.02 55)",
        "--destructive": "oklch(0.6 0.28 25)",
        "--destructive-foreground": "oklch(0.99 0 0)",
        "--border": "oklch(0.85 0.05 65)",
        "--input": "oklch(0.85 0.05 65)",
        "--ring": "oklch(0.55 0.18 65)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.55 0.18 65)",
      },
      dark: {
        "--background": "oklch(0.08 0.01 65)",
        "--foreground": "oklch(0.95 0 0)", // White text for readability
        "--card": "oklch(0.12 0.02 65)",
        "--card-foreground": "oklch(0.95 0 0)", // White text
        "--popover": "oklch(0.12 0.02 65)",
        "--popover-foreground": "oklch(0.95 0 0)", // White text
        "--primary": "oklch(0.75 0.15 65)", // P3 Amber
        "--primary-foreground": "oklch(0.15 0.02 65)", // Darkened for WCAG AA contrast on bright primary
        "--secondary": "oklch(0.53 0.12 50)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.95 0 0)", // White text
        "--muted": "oklch(0.2 0.03 65)",
        "--muted-foreground": "oklch(0.7 0 0)", // Light gray text
        "--accent": "oklch(0.7 0.14 55)",
        "--accent-foreground": "oklch(0.95 0 0)", // White text
        "--destructive": "oklch(0.55 0.28 25)",
        "--destructive-foreground": "oklch(0.95 0 0)",
        "--border": "oklch(0.25 0.05 65)",
        "--input": "oklch(0.25 0.05 65)",
        "--ring": "oklch(0.75 0.15 65)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.75 0.15 65)",
      },
    },
  },
  blue: {
    name: "Blue (P7)",
    description: "Deep P7 blue terminal display",
    gradient: {
      light:
        "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.85 0.15 240 / 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.85 0.12 240 / 0.2), transparent)",
      dark: "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.3 0.15 240 / 0.4), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.25 0.12 240 / 0.3), transparent)",
    },
    animatedBackground: {
      light: {
        type: "terrain",
        primaryColor: "oklch(0.48 0.18 240)",
      },
      dark: {
        type: "crt",
        primaryColor: "oklch(0.73 0.14 240)",
      },
    },
    cssVars: {
      light: {
        "--background": "oklch(0.98 0.01 240)",
        "--foreground": "oklch(0.15 0.02 240)",
        "--card": "oklch(1 0 0)",
        "--card-foreground": "oklch(0.15 0.02 240)",
        "--popover": "oklch(1 0 0)",
        "--popover-foreground": "oklch(0.15 0.02 240)",
        "--primary": "oklch(0.48 0.18 240)", // P7 Blue
        "--primary-foreground": "oklch(0.99 0 0)",
        "--secondary": "oklch(0.54 0.14 240)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.99 0 0)",
        "--muted": "oklch(0.94 0.02 240)",
        "--muted-foreground": "oklch(0.5 0.05 240)",
        "--accent": "oklch(0.63 0.16 240)",
        "--accent-foreground": "oklch(0.15 0.02 240)",
        "--destructive": "oklch(0.6 0.28 25)",
        "--destructive-foreground": "oklch(0.99 0 0)",
        "--border": "oklch(0.85 0.05 240)",
        "--input": "oklch(0.85 0.05 240)",
        "--ring": "oklch(0.48 0.18 240)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.48 0.18 240)",
      },
      dark: {
        "--background": "oklch(0.08 0.01 240)",
        "--foreground": "oklch(0.95 0 0)", // White text for readability
        "--card": "oklch(0.12 0.02 240)",
        "--card-foreground": "oklch(0.95 0 0)", // White text
        "--popover": "oklch(0.12 0.02 240)",
        "--popover-foreground": "oklch(0.95 0 0)", // White text
        "--primary": "oklch(0.73 0.14 240)", // P7 Blue
        "--primary-foreground": "oklch(0.15 0.02 240)", // Darkened for WCAG AA contrast on bright primary
        "--secondary": "oklch(0.52 0.11 240)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.95 0 0)", // White text
        "--muted": "oklch(0.2 0.03 240)",
        "--muted-foreground": "oklch(0.7 0 0)", // Light gray text
        "--accent": "oklch(0.63 0.13 240)",
        "--accent-foreground": "oklch(0.95 0 0)", // White text
        "--destructive": "oklch(0.55 0.28 25)",
        "--destructive-foreground": "oklch(0.95 0 0)",
        "--border": "oklch(0.25 0.05 240)",
        "--input": "oklch(0.25 0.05 240)",
        "--ring": "oklch(0.73 0.14 240)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.73 0.14 240)",
      },
    },
  },
  white: {
    name: "White (P4)",
    description: "Crisp P4 white monochrome display",
    gradient: {
      light:
        "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.85 0.05 240 / 0.2), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.85 0.03 240 / 0.15), transparent)",
      dark: "radial-gradient(ellipse 80% 50% at 50% -20%, oklch(0.25 0.05 240 / 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 60%, oklch(0.2 0.03 240 / 0.25), transparent)",
    },
    animatedBackground: {
      light: {
        type: "terrain",
        primaryColor: "oklch(0.35 0.05 240)",
      },
      dark: {
        type: "crt",
        primaryColor: "oklch(0.92 0.02 240)",
      },
    },
    cssVars: {
      light: {
        "--background": "oklch(0.98 0.005 240)",
        "--foreground": "oklch(0.15 0.01 240)",
        "--card": "oklch(1 0 0)",
        "--card-foreground": "oklch(0.15 0.01 240)",
        "--popover": "oklch(1 0 0)",
        "--popover-foreground": "oklch(0.15 0.01 240)",
        "--primary": "oklch(0.35 0.05 240)", // P4 White (dark gray)
        "--primary-foreground": "oklch(0.99 0 0)",
        "--secondary": "oklch(0.52 0.03 240)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.99 0 0)",
        "--muted": "oklch(0.94 0.01 240)",
        "--muted-foreground": "oklch(0.5 0.02 240)",
        "--accent": "oklch(0.65 0.04 240)",
        "--accent-foreground": "oklch(0.15 0.01 240)",
        "--destructive": "oklch(0.6 0.28 25)",
        "--destructive-foreground": "oklch(0.99 0 0)",
        "--border": "oklch(0.85 0.02 240)",
        "--input": "oklch(0.85 0.02 240)",
        "--ring": "oklch(0.35 0.05 240)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.35 0.05 240)",
      },
      dark: {
        "--background": "oklch(0.08 0.005 240)",
        "--foreground": "oklch(0.95 0 0)", // White text for readability
        "--card": "oklch(0.12 0.01 240)",
        "--card-foreground": "oklch(0.95 0 0)", // White text
        "--popover": "oklch(0.12 0.01 240)",
        "--popover-foreground": "oklch(0.95 0 0)", // White text
        "--primary": "oklch(0.92 0.02 240)", // P4 White
        "--primary-foreground": "oklch(0.15 0.01 240)", // Darkened for WCAG AA contrast on bright primary
        "--secondary": "oklch(0.52 0.015 240)", // Darkened for WCAG AA compliance (4.5:1)
        "--secondary-foreground": "oklch(0.95 0 0)", // White text
        "--muted": "oklch(0.2 0.01 240)",
        "--muted-foreground": "oklch(0.75 0 0)", // Light gray text, WCAG AA compliant on dark bg
        "--accent": "oklch(0.75 0.02 240)",
        "--accent-foreground": "oklch(0.95 0 0)", // White text
        "--destructive": "oklch(0.55 0.28 25)",
        "--destructive-foreground": "oklch(0.95 0 0)",
        "--border": "oklch(0.25 0.015 240)",
        "--input": "oklch(0.25 0.015 240)",
        "--ring": "oklch(0.92 0.02 240)",
        "--radius": "1rem",
        "--terrain-color": "oklch(0.92 0.02 240)",
      },
    },
  },
};

/**
 * Pre-calculated CSS text cache for all themes.
 * Generated once at module initialization for optimal runtime performance.
 */
export const themeStyleCache: Record<DesignTheme, ThemeStyleCache> = {
  green: generateThemeStyles(designThemes.green),
  amber: generateThemeStyles(designThemes.amber),
  blue: generateThemeStyles(designThemes.blue),
  white: generateThemeStyles(designThemes.white),
};
